import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { adminApi, adminToken } from "@/lib/api";
import { ContentManager } from "./AdminCMS";
import { PurchasesView, SupportView, ReadersView, DataView } from "./AdminExtras";
import { LaunchView, LeadsView } from "./AdminLaunch";
import { ReviewsView } from "./AdminReviews";

const STATUSES = ["new", "contacted", "confirmed", "closed"];
const fmt = (iso) => new Date(iso).toLocaleString("en-SG", { dateStyle: "medium", timeStyle: "short" });
const detail = (e) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : "Something went wrong. Please try again."; };

const Login = ({ onDone }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try { const { token } = await adminApi.login(password); adminToken.set(token); onDone(); }
    catch (err) { setError(detail(err)); }
    finally { setBusy(false); }
  };
  return (
    <div className="admin-login" data-testid="admin-login-page">
      <form className="admin-login-card" onSubmit={submit}>
        <img alt="TransitionWorks — From Potential to Performance" src="/assets/tw-dashboard-logo.png" />
        <span className="eyebrow">Scott's dashboard</span>
        <h1>Bookings at a glance</h1>
        <label className="form-field"><span>Password</span>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter the dashboard password" autoFocus data-testid="admin-password-input" /></label>
        <div className="form-error" data-testid="admin-login-error">{error}</div>
        <button className="btn btn-gold btn-block" type="submit" disabled={busy || !password} data-testid="admin-login-btn">{busy ? "Signing in..." : "Open dashboard"}</button>
        <Link to="/" className="admin-back">← Back to the website</Link>
      </form>
    </div>
  );
};

const Stat = ({ label, value, sub, testId }) => (
  <div className="admin-stat" data-testid={testId}><span>{label}</span><strong>{value}</strong>{sub && <small>{sub}</small>}</div>
);

const NoteCell = ({ booking, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(booking.note || "");
  const [busy, setBusy] = useState(false);
  const save = async () => { setBusy(true); await onSave(text.trim()); setBusy(false); setEditing(false); };
  if (!editing) {
    return (
      <div className="admin-note" data-testid={`booking-note-${booking.id}`}>
        {booking.note ? <p>{booking.note}</p> : <span className="admin-muted">No private notes</span>}
        <button className="admin-link" onClick={() => { setText(booking.note || ""); setEditing(true); }} data-testid={`booking-note-edit-${booking.id}`}>{booking.note ? "Edit note" : "Add note"}</button>
      </div>
    );
  }
  return (
    <div className="admin-note" data-testid={`booking-note-editor-${booking.id}`}>
      <textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="Private note for Scott only" data-testid={`booking-note-input-${booking.id}`} />
      <div className="admin-note-actions">
        <button className="admin-link" onClick={save} disabled={busy} data-testid={`booking-note-save-${booking.id}`}>{busy ? "Saving..." : "Save"}</button>
        <button className="admin-link admin-link-muted" onClick={() => setEditing(false)} data-testid={`booking-note-cancel-${booking.id}`}>Cancel</button>
      </div>
    </div>
  );
};

const Dashboard = ({ onLogout }) => {
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [mainView, setMainView] = useState("bookings");
  const [cmsTab, setCmsTab] = useState("general");
  const [siteMode, setSiteMode] = useState(null);
  const [pay, setPay] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const [s, b, l, ps] = await Promise.all([adminApi.stats(), adminApi.bookings(), adminApi.launch(), adminApi.getStripe().catch(() => null)]); setStats(s); setRows(b); setSiteMode(l.mode); setPay(ps); }
    catch (err) { if (err?.response?.status === 401) onLogout(); else toast.error(detail(err)); }
    finally { setLoading(false); }
  }, [onLogout]);
  useEffect(() => { load(); }, [load]);
  const goCms = (tab) => { setCmsTab(tab); setMainView("content"); };

  const setStatus = async (id, status) => {
    try { const updated = await adminApi.updateBooking(id, { status }); setRows((r) => r.map((x) => (x.id === id ? updated : x))); toast.success(`Marked as ${status}`); load(); }
    catch (err) { toast.error(detail(err)); }
  };
  const saveNote = async (b, note) => {
    try { const updated = await adminApi.updateBooking(b.id, { status: b.status, note }); setRows((r) => r.map((x) => (x.id === b.id ? updated : x))); toast.success("Note saved"); }
    catch (err) { toast.error(detail(err)); }
  };
  const exportCsv = async () => {
    try {
      const blob = await adminApi.exportCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `transitionworks-bookings-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success("Spreadsheet downloaded");
    } catch (err) { toast.error(detail(err)); }
  };

  const visible = rows.filter((r) => (filter === "all" || r.status === filter) &&
    (!q || `${r.fullName} ${r.email} ${r.contactNumber} ${r.discussionTopic || ""} ${r.note || ""}`.toLowerCase().includes(q.toLowerCase())));

  return (
    <div className="admin-page" data-testid="admin-dashboard">
      <header className="admin-header">
        <div className="container admin-header-inner">
          <Link to="/"><img alt="TransitionWorks" src="/assets/logo-dark.png" /></Link>
          <div className="admin-view-tabs">
            <button className={`admin-view-tab${mainView === "bookings" ? " active" : ""}`} onClick={() => setMainView("bookings")} data-testid="admin-view-bookings">Bookings</button>
            <button className={`admin-view-tab${mainView === "purchases" ? " active" : ""}`} onClick={() => setMainView("purchases")} data-testid="admin-view-purchases">Purchases</button>
            <button className={`admin-view-tab${mainView === "readers" ? " active" : ""}`} onClick={() => setMainView("readers")} data-testid="admin-view-readers">Readers</button>
            <button className={`admin-view-tab${mainView === "leads" ? " active" : ""}`} onClick={() => setMainView("leads")} data-testid="admin-view-leads">Leads</button>
            <button className={`admin-view-tab${mainView === "reviews" ? " active" : ""}`} onClick={() => setMainView("reviews")} data-testid="admin-view-reviews">Reviews</button>
            <button className={`admin-view-tab${mainView === "support" ? " active" : ""}`} onClick={() => setMainView("support")} data-testid="admin-view-support">Support</button>
            <button className={`admin-view-tab${mainView === "content" ? " active" : ""}`} onClick={() => setMainView("content")} data-testid="admin-view-content">Edit website</button>
            <button className={`admin-view-tab${mainView === "data" ? " active" : ""}`} onClick={() => setMainView("data")} data-testid="admin-view-data">Data</button>
            <button className={`admin-view-tab admin-view-tab-launch${mainView === "launch" ? " active" : ""}`} onClick={() => setMainView("launch")} data-testid="admin-view-launch">
              <i className={`mode-dot ${siteMode || ""}`} />Launch
            </button>
          </div>
          <div className="admin-header-actions">
            {pay && (() => {
              const info = !pay.configured ? { t: "Payments: off", bg: "rgba(255,255,255,.08)", c: "#b8c1bc" }
                : pay.mode === "live" ? { t: "Payments: LIVE ✓", bg: "rgba(53,208,127,.16)", c: "#6ee7a8" }
                : pay.mode === "test" ? { t: "Payments: TEST", bg: "rgba(224,168,58,.18)", c: "#ffcc66" }
                : { t: "Payments: ON", bg: "rgba(255,255,255,.1)", c: "#e6ece9" };
              return <button type="button" className="mode-pill" onClick={() => goCms("payments")} data-testid="admin-payments-pill" title={`Stripe ${pay.mode} mode · source: ${pay.source}`} style={{ background: info.bg, color: info.c }}>{info.t}</button>;
            })()}
            {siteMode && <button type="button" className={`mode-pill ${siteMode}`} onClick={() => setMainView("launch")} data-testid="admin-mode-pill">{siteMode === "live" ? "Site is LIVE" : "Coming Soon mode"}</button>}
            {mainView === "bookings" && <>
              <button className="admin-ghost" onClick={exportCsv} data-testid="admin-export-btn">Export spreadsheet</button>
              <button className="admin-ghost" onClick={load} data-testid="admin-refresh-btn">Refresh</button>
            </>}
            <button className="admin-ghost" onClick={onLogout} data-testid="admin-logout-btn">Sign out</button>
          </div>
        </div>
      </header>
      {mainView === "content" ? (
        <main className="container admin-main"><ContentManager onUnauthorized={onLogout} initialTab={cmsTab} /></main>
      ) : mainView === "purchases" ? (
        <main className="container admin-main"><PurchasesView onUnauthorized={onLogout} /></main>
      ) : mainView === "readers" ? (
        <main className="container admin-main"><ReadersView onUnauthorized={onLogout} /></main>
      ) : mainView === "leads" ? (
        <main className="container admin-main"><LeadsView onUnauthorized={onLogout} /></main>
      ) : mainView === "reviews" ? (
        <main className="container admin-main"><ReviewsView onUnauthorized={onLogout} /></main>
      ) : mainView === "launch" ? (
        <main className="container admin-main"><LaunchView onUnauthorized={onLogout} onGoTab={goCms} onModeChange={setSiteMode} /></main>
      ) : mainView === "support" ? (
        <main className="container admin-main"><SupportView onUnauthorized={onLogout} /></main>
      ) : mainView === "data" ? (
        <main className="container admin-main"><DataView onUnauthorized={onLogout} /></main>
      ) : (
      <main className="container admin-main">
        <div className="admin-title">
          <span className="eyebrow">5Ps Conversation requests</span>
          <h1>Bookings dashboard</h1>
        </div>
        <div className="admin-stats">
          <Stat label="Total requests" value={stats?.bookings_total ?? "–"} testId="stat-total" />
          <Stat label="Awaiting contact" value={stats?.bookings_new ?? "–"} sub="status: new" testId="stat-new" />
          <Stat label="Last 7 days" value={stats?.bookings_week ?? "–"} testId="stat-week" />
          <Stat label="eBooks sold" value={stats?.ebooks_sold ?? "–"} sub={stats ? `S$${stats.revenue_sgd.toFixed(2)} revenue` : ""} testId="stat-sold" />
        </div>
        <div className="admin-toolbar">
          <div className="admin-filters">
            {["all", ...STATUSES].map((s) => <button key={s} className={`filter-btn${filter === s ? " active" : ""}`} onClick={() => setFilter(s)} data-testid={`admin-filter-${s}`}>{s[0].toUpperCase() + s.slice(1)}</button>)}
          </div>
          <label className="searchbox"><span>⌕</span><input type="search" placeholder="Search name, email, phone, topic" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-search-input" /></label>
        </div>
        <div className="admin-table-wrap">
          {loading && !rows.length ? <div className="admin-empty">Loading bookings...</div> : !visible.length ? <div className="admin-empty" data-testid="admin-empty">No bookings match this view yet.</div> : (
            <table className="admin-table" data-testid="admin-bookings-table">
              <thead><tr><th>Received</th><th>Visitor</th><th>Contact</th><th>Residency</th><th>Preferred slot</th><th>Topic</th><th>Private notes</th><th>Status</th></tr></thead>
              <tbody>
                {visible.map((b) => (
                  <tr key={b.id} data-testid={`booking-row-${b.id}`}>
                    <td className="admin-muted">{fmt(b.createdAt)}</td>
                    <td><strong>{b.fullName}</strong></td>
                    <td><a href={`mailto:${b.email}`}>{b.email}</a><br /><a href={`tel:${b.contactNumber}`} className="admin-muted">{b.contactNumber}</a></td>
                    <td><span className="admin-pill">{b.residency}</span></td>
                    <td><strong>{b.preferredDate}</strong><br /><span className="admin-muted">{b.preferredTime}</span></td>
                    <td className="admin-topic">{b.discussionTopic || <span className="admin-muted">No topic given</span>}{b.reminderSent && <><br /><span className="admin-pill">Reminder sent</span></>}</td>
                    <td><NoteCell booking={b} onSave={(note) => saveNote(b, note)} /></td>
                    <td>
                      <select className={`admin-status status-${b.status}`} value={b.status} onChange={(e) => setStatus(b.id, e.target.value)} data-testid={`booking-status-${b.id}`}>
                        {STATUSES.map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
      )}
    </div>
  );
};

export default function Admin() {
  const [authed, setAuthed] = useState(!!adminToken.get());
  const logout = useCallback(() => { adminToken.clear(); setAuthed(false); }, []);
  return authed ? <Dashboard onLogout={logout} /> : <Login onDone={() => setAuthed(true)} />;
}
